import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StatementRow, decisionEnum } from 'src/services/statements/models'
import { Button } from 'src/components/button'
import { cn } from 'src/utils/style'
import { ExpenseSearchPicker } from './expense-search-picker'

interface MatchedRowCardProps {
  row: StatementRow
  onApprove: () => void
  onReject: () => void
  onUnlink: () => void
  onLinkExpense: (expenseId: string) => void
  isReadOnly: boolean
  isLinking: boolean
  isApproving?: boolean
  isRejecting?: boolean
  isUnlinking?: boolean
  selected?: boolean
  onSelectChange?: (selected: boolean) => void
}

export const MatchedRowCard = ({
  row,
  onApprove,
  onReject,
  onUnlink,
  onLinkExpense,
  isReadOnly,
  isLinking,
  isApproving,
  isRejecting,
  isUnlinking,
  selected,
  onSelectChange,
}: MatchedRowCardProps) => {
  const { t } = useTranslation()
  const raw = row.rawData
  const expense = row.matchedExpenseRef
  const isApproved = row.decision === decisionEnum.APPROVED

  const [showChangePicker, setShowChangePicker] = useState(false)

  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-surface px-6 py-4 shadow-sm',
        isApproved ? 'border border-green-200' : '',
      )}
      data-testid={`matched-row-${row.id}`}
    >
      <div className="flex flex-col gap-4 sm:flex-row">
        {/* Checkbox for bulk selection */}
        {!isReadOnly && onSelectChange && !isApproved && (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selected ?? false}
              onChange={(e) => onSelectChange(e.target.checked)}
              className="h-4 w-4 rounded border-border-strong text-primary-500 focus:ring-primary-500"
              data-testid={`select-row-${row.id}`}
            />
          </div>
        )}
        {/* Statement row data */}
        <div className="flex-1">
          <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            {t('statementReview.statementLabel')}
          </p>
          <p className="mt-1 font-medium text-text-primary">{raw.description}</p>
          <div className="mt-1 flex gap-4 text-sm text-text-placeholder">
            <span>{raw.date}</span>
            <span className="font-medium">${Number(raw.amount).toFixed(2)}</span>
          </div>
          {/* Confidence is an AI signal: shown only alongside LLM reasoning. When
              llmReasoning is null the row was matched deterministically, so no
              confidence percentage is rendered. */}
          {row.llmReasoning != null && row.confidenceScore != null && (
            <p className="mt-1 text-xs text-text-tertiary">
              {t('statementReview.confidence', { value: (row.confidenceScore * 100).toFixed(0) })}
            </p>
          )}
        </div>

        {/* Arrow */}
        <div className="hidden items-center text-gray-300 sm:flex">
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>

        {/* Matched expense */}
        <div className="flex-1">
          <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
            {t('statementReview.rapaExpense')}
          </p>
          {expense ? (
            <a
              href={`/expenses/expense/${row.matchedExpense}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 block text-accent-700 underline decoration-accent-300 underline-offset-2 hover:decoration-accent-600"
              data-testid={`expense-link-${row.id}`}
            >
              <p className="font-medium">{expense.name}</p>
              <div className="mt-1 flex gap-4 text-sm opacity-80">
                <span>{expense.expenseDate}</span>
                <span className="font-medium">
                  ${Number(expense.amount).toFixed(2)} {expense.amountCurrency}
                </span>
              </div>
              {expense.reportsRef && expense.reportsRef.length > 0 && (
                <p className="mt-1 text-xs opacity-60">
                  {t('statementReview.reportLabel', { name: expense.reportsRef[0].name })}
                </p>
              )}
            </a>
          ) : (
            <p className="mt-1 text-sm text-text-tertiary">
              {t('statementReview.noExpenseLinked')}
            </p>
          )}
        </div>

        {/* Actions */}
        {!isReadOnly && (
          <div className="flex items-center gap-2">
            {isApproved ? (
              <>
                <span
                  className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700"
                  data-testid={`approved-badge-${row.id}`}
                >
                  {t('common.approved')}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  shape="square"
                  onClick={onReject}
                  disabled={isRejecting}
                  className="font-medium transition-colors"
                  data-testid={`reject-${row.id}`}
                >
                  {t('common.reject')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  shape="square"
                  onClick={onApprove}
                  disabled={isApproving}
                  className="font-medium transition-colors"
                  data-testid={`approve-${row.id}`}
                >
                  {t('common.approve')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  shape="square"
                  onClick={onReject}
                  disabled={isRejecting}
                  className="font-medium transition-colors"
                  data-testid={`reject-${row.id}`}
                >
                  {t('common.reject')}
                </Button>
              </>
            )}
            {expense && (
              <Button
                variant="secondary"
                size="sm"
                shape="square"
                onClick={onUnlink}
                disabled={isUnlinking}
                className="font-medium transition-colors"
                data-testid={`unlink-${row.id}`}
              >
                {t('common.unlink')}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Change Match link */}
      {!isReadOnly && (
        <div className="mt-2">
          <Button
            variant="text"
            tone="neutral"
            onClick={() => setShowChangePicker(!showChangePicker)}
            className="text-xs underline"
            data-testid={`change-match-${row.id}`}
          >
            {t('statementReview.changeMatch')}
          </Button>
        </div>
      )}

      {/* Change match picker */}
      {!isReadOnly && showChangePicker && (
        <div className="mt-2">
          <ExpenseSearchPicker onSelect={onLinkExpense} isLinking={isLinking} />
        </div>
      )}
    </div>
  )
}
